import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Clock, MapPin, Sparkles, CreditCard } from 'lucide-react';
import PillarLayout from '../pillar/PillarLayout';
import CheckoutModal from '../CheckoutModal';

const CANONICAL = 'https://enbet-consulting.de/ki-ready-check';

const bullets = [
  { t: 'Echte Daten statt Standard-Templates', s: 'Individuelle Analyse eures Betriebs – keine automatisierten Textbausteine.' },
  { t: 'Strukturierte Prozess-Diagnose', s: 'Glasklare Identifikation eurer operativen Reibungspunkte, Medienbrüche und täglichen Engpässe.' },
  { t: 'Quantifizierte Verlustzeiten', s: 'Wir beziffern das ungenutzte Zeitpotenzial pro Abteilung in echten Zahlen.' },
  { t: 'Objektive Wirtschaftlichkeitsprüfung', s: 'Ehrliche, herstellerneutrale Einordnung, welche KI-Anwendungen für euch profitabel sind.' },
  { t: 'Maximale Investitionssicherheit', s: 'Schutz vor kostspieligen Fehlentscheidungen und teuren Folgekosten.' },
];

const steps = [
  { n: '01', t: '20-Minuten Kennenlernen', s: 'Kostenloser Erstkontakt mit Sebastian – wir prüfen ob der Ready Check für deinen Betrieb passt.' },
  { n: '02', t: 'Daten erheben', s: 'Du bekommst einen sicheren Magic-Link. Branchenspezifischer Diagnose-Chatbot erhebt eure operativen Schmerzpunkte in 30–45 Minuten – ortsunabhängig.' },
  { n: '03', t: 'Analyse + Audit-Report', s: 'Sebastian analysiert die erhobenen Verlustzeiten und erstellt euren herstellerneutralen Audit-Report mit ROI-Kalkulation.' },
  { n: '04', t: 'Ergebnis-Übergabe', s: 'Persönliche Übergabe via Online-Termin. Du bekommst Report als PDF + Markdown – nutzbar als Entscheidungsgrundlage für eure Geschäftsleitung.' },
];

const faqItems = [
  {
    q: 'Wie lange dauert der KI Ready Check insgesamt?',
    a: 'Vom Buchungstermin bis zur Report-Übergabe planst du 1–2 Wochen ein. Der eigentliche Diagnose-Chat ist in 30–45 Minuten erledigt, danach braucht Sebastian 5–7 Tage für die Analyse und den finalen Audit-Report.',
  },
  {
    q: 'Was bekomme ich konkret als Ergebnis?',
    a: 'Einen strukturierten Audit-Report im McKinsey-Stil (10–15 Seiten PDF + Markdown): Executive Summary, erhobene Schmerzpunkte je Abteilung, quantifizierte Verlustzeiten, ROI-Rechnung, herstellerneutrale Quick-Wins, 3-Phasen-Roadmap-Vorschlag, Disclaimer.',
  },
  {
    q: 'Was unterscheidet den Ready Check von einer „normalen" Beratung?',
    a: 'Drei Punkte. (1) Festpreis 850 € – kein Tagessatz, kein Aufwand-Wuchern. (2) Datenbasiert: Verlustzeiten werden mit Zahlen belegt, nicht erraten. (3) Herstellerneutral: wir empfehlen keine Tools, sondern zeigen wo KI sich rentiert und wo nicht.',
  },
  {
    q: 'Bekomme ich danach Tool-Empfehlungen?',
    a: 'Nein – und das ist Absicht. Der Ready Check ist die Diagnose. Konkrete Tool-Empfehlungen gehören in den Strategie-Workshop (Modul 2), weil sie auf eurer spezifischen Datenlage und IT-Architektur basieren müssen. Wir wollen vermeiden, dass ihr Tools kauft, die nicht greifen.',
  },
  {
    q: 'Ist das auch für kleine Unternehmen sinnvoll?',
    a: 'Ja. Der Festpreis 850 € macht den Check gerade für KMU mit 5–50 Mitarbeitern niederschwellig. Wer ohne Standortbestimmung KI-Tools kauft, verbrennt typischerweise das 20–50-fache.',
  },
];

export default function LandingReadyCheck() {
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const schemaLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${CANONICAL}#service`,
        name: 'KI Ready Check',
        provider: { '@id': 'https://enbet-consulting.de/#organization' },
        serviceType: 'Operative KI-Bestandsaufnahme und Potenzialanalyse',
        description: 'Datenbasierte Diagnose der operativen Schmerzpunkte und KI-Potenziale für stationären Handel und Filialisten. Festpreis 850 €.',
        offers: {
          '@type': 'Offer',
          price: '850',
          priceCurrency: 'EUR',
          availability: 'https://schema.org/InStock',
          url: CANONICAL,
        },
        areaServed: 'DE',
        inLanguage: 'de-DE',
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://enbet-consulting.de/' },
          { '@type': 'ListItem', position: 2, name: 'KI Ready Check', item: CANONICAL },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <PillarLayout
      title="KI Ready Check – Festpreis 850 € | ENBET Consulting"
      description="Der KI Ready Check: ehrliche Diagnose der operativen Schmerzpunkte deines Betriebs in 1–2 Wochen. Quantifizierte Verlustzeiten, herstellerneutraler Audit-Report, 850 € Festpreis."
      canonical={CANONICAL}
      schemaLd={schemaLd}
    >
      {/* Hero */}
      <section className="bg-[#0a0a0a] pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="text-[#befa0a] text-xs uppercase tracking-[0.3em] mb-5 font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Modul 01 · KI Ready Check
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="text-white text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Die ungefilterte Wahrheit für dein Unternehmen.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="text-white/70 text-base md:text-lg leading-relaxed max-w-2xl mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Bevor du Geld in KI-Tools steckst, brauchst du eine ehrliche Diagnose: Wo verlierst du täglich Zeit, wo lohnt sich KI wirklich – und wo wäre sie rausgeschmissenes Geld. Der KI Ready Check liefert genau das. Datenbasiert, herstellerneutral, in 1–2 Wochen.
          </motion.p>

          {/* Preis-Block */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="inline-flex flex-col items-start gap-1 border border-[#befa0a]/30 bg-[#befa0a]/5 px-6 py-4 mb-8">
            <div className="inline-flex items-baseline gap-4">
              <span className="text-[#befa0a] text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>850 €</span>
              <span className="text-white/60 text-sm uppercase tracking-wider" style={{ fontFamily: "'Oswald', sans-serif" }}>Festpreis · alles inklusive</span>
            </div>
            <span className="text-white/40 text-[11px]">netto, zzgl. 19 % MwSt</span>
          </motion.div>

          {/* Doppel-CTA */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4">
            <Link to="/#booking"
              className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Check size={16} /> Kostenloses Erstgespräch
            </Link>
            <button type="button" onClick={() => setCheckoutOpen(true)}
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <CreditCard size={16} /> Direkt buchen (850 €)
            </button>
          </motion.div>
          <p className="text-white/40 text-xs mt-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Sichere Bezahlung über Mollie · SEPA · Kreditkarte · Klarna
          </p>
          <p className="text-white/60 text-sm mt-6" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Direkt Modul 1 + Modul 2 buchen?{' '}
            <Link to="/ki-kombi-paket" className="text-[#befa0a] hover:underline font-bold">
              Kombi-Paket 2.900 € netto (1.450 € sparen) →
            </Link>
          </p>
        </div>
      </section>

      {/* Pain-Block */}
      <section className="bg-[#f7f7f7] py-16 md:py-20">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Warum überhaupt ein Ready Check?
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-[1.15] mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Garbage in, garbage out.
          </h2>
          <p className="text-[#1f1f1f]/75 text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
            KI ist nur so gut wie die Datenbasis, auf der sie steht. Wer KI ohne saubere Standortbestimmung einsetzt, verbrennt Budget.
          </p>
          <p className="text-[#1f1f1f]/65 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Stell dir vor, ein Filialbetrieb kauft sich eine KI-gestützte Personalplanung. Klingt nach Effizienz. Aber: Die Frontline-Daten sind chaotisch, niemand pflegt sie, jede Filiale hat ihren eigenen Excel-Stand. Die KI gibt Empfehlungen aus Mülldaten – und das Team verliert das Vertrauen in 4 Wochen. Geld weg, Akzeptanz weg, KI-Projekt verbrannt.
          </p>
          <p className="text-[#1f1f1f] text-base md:text-lg leading-relaxed font-medium mt-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Der Ready Check ist die günstigste Versicherung gegen genau diesen Fehler. 850 € investiert spart leicht 20.000–50.000 € verbranntes Tool-Budget.
          </p>
        </div>
      </section>

      {/* Was du bekommst */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Was du bekommst
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-12" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Fünf Bausteine, ein klares Ergebnis.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {bullets.map((b, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-[#e5e5e5] p-6 flex gap-4">
                <div className="w-10 h-10 rounded-full bg-[#0a0a0a] text-[#befa0a] flex items-center justify-center flex-shrink-0 text-sm font-bold" style={{ fontFamily: "'Oswald', sans-serif" }}>
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="flex-1">
                  <h3 className="text-[#1f1f1f] text-base md:text-lg font-bold uppercase tracking-tight mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>{b.t}</h3>
                  <p className="text-[#1f1f1f]/70 text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{b.s}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Prozess */}
      <section className="bg-[#0a0a0a] py-16 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#befa0a] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            So läuft's ab
          </p>
          <h2 className="text-white text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-12" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Vier Schritte, 1–2 Wochen, fertig.
          </h2>
          <div className="space-y-6">
            {steps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                className="flex gap-6 border-b border-[#1a1a1a] pb-6">
                <span className="text-[#befa0a] text-3xl md:text-4xl font-bold flex-shrink-0" style={{ fontFamily: "'Oswald', sans-serif" }}>{s.n}</span>
                <div className="flex-1">
                  <h3 className="text-white text-lg md:text-xl font-bold uppercase tracking-tight mb-2" style={{ fontFamily: "'Oswald', sans-serif" }}>{s.t}</h3>
                  <p className="text-white/65 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.s}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Für wen */}
      <section className="py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Für wen
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-8" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Wenn das auf dich zutrifft, ist der Check für dich gemacht.
          </h2>
          <ul className="space-y-3 text-[#1f1f1f]/75 text-base md:text-lg" style={{ fontFamily: "'Manrope', sans-serif" }}>
            {[
              'Du betreibst stationäre Geschäfte oder Filialen mit Frontline-Teams.',
              'Du spürst, dass viele Stunden im Tagesgeschäft an Routinen verloren gehen – kannst es aber nicht beziffern.',
              'Du hast schon mal KI-Tools getestet, aber nichts ist „angekommen".',
              'Du willst nicht das nächste Tool kaufen, sondern erst verstehen wo es sich lohnt.',
              'Du brauchst eine Entscheidungsgrundlage für die Geschäftsleitung oder den Beirat.',
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-3">
                <Check size={18} className="text-[#befa0a] mt-1 flex-shrink-0" />
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#f5f5f5] py-16 md:py-24">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <p className="text-[#e07a2f] text-xs uppercase tracking-[0.25em] font-bold mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Häufige Fragen
          </p>
          <h2 className="text-[#1f1f1f] text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-10" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Was du wahrscheinlich noch fragst.
          </h2>
          <div className="space-y-8">
            {faqItems.map((f, i) => (
              <div key={i}>
                <h3 className="text-[#1f1f1f] text-lg md:text-xl font-bold uppercase tracking-tight mb-3" style={{ fontFamily: "'Oswald', sans-serif" }}>{f.q}</h3>
                <p className="text-[#1f1f1f]/70 text-base leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Garantie / Vertrauen */}
      <section className="py-16 md:py-20">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <Sparkles size={32} className="text-[#e07a2f] mx-auto mb-5" />
          <h2 className="text-[#1f1f1f] text-2xl md:text-3xl font-bold uppercase tracking-tight leading-tight mb-4" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Persönliche Begleitung, kein Skript.
          </h2>
          <p className="text-[#1f1f1f]/70 text-base md:text-lg leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Der Ready Check ist keine Automaten-Lösung. Sebastian Funke begleitet jede Diagnose persönlich – aus 100+ skalierten Retail-Standorten und ca. 9 Mio. Monatsumsatz im Retail-Setup. Wenn es im Erstgespräch nicht passt, sagen wir das ehrlich vor dem Vertrag.
          </p>
          <div className="flex items-center justify-center gap-2 text-[#1f1f1f]/50 text-sm mt-5" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <MapPin size={14} /> Schauenburg bei Kassel · deutschlandweit
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#0a0a0a] py-16 md:py-20">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <h2 className="text-white text-2xl md:text-4xl font-bold uppercase tracking-tight leading-tight mb-6" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Bereit für Klarheit?
          </h2>
          <p className="text-white/60 text-base md:text-lg mb-8 max-w-xl mx-auto" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Erst Erstgespräch, dann Check. So stellen wir sicher, dass die 850 € auch wirklich für dich Sinn machen.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/#booking"
              className="inline-flex items-center justify-center gap-2 bg-[#befa0a] text-[#0a0a0a] px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Check size={16} /> Erstgespräch buchen
            </Link>
            <button type="button" onClick={() => setCheckoutOpen(true)}
              className="inline-flex items-center justify-center gap-2 border border-white/30 text-white px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] hover:bg-white hover:text-[#0a0a0a] transition-colors"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <CreditCard size={16} /> Direkt buchen (850 €)
            </button>
          </div>
          <div className="mt-12 pt-8 border-t border-[#1a1a1a] flex items-center justify-center gap-3 text-white/40 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
            <Clock size={14} /> 1–2 Wochen bis Report
            <span className="text-white/20">·</span>
            <span>850 € Festpreis</span>
            <span className="text-white/20">·</span>
            <span>herstellerneutral</span>
          </div>
        </div>
      </section>

      {/* Weiterführende Links */}
      <section className="py-12 bg-white">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <p className="text-[#1f1f1f]/50 text-xs uppercase tracking-[0.25em] font-bold mb-5" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Verwandte Themen
          </p>
          <div className="flex flex-wrap gap-3">
            {[
              { to: '/ki-strategie-workshop', label: 'KI Strategie Workshop (Modul 2)' },
              { to: '/ki-im-stationaeren-handel', label: 'KI im stationären Handel' },
              { to: '/ki-fuer-filialisten', label: 'KI für Filialisten' },
              { to: '/ki-beratung-kassel', label: 'KI-Beratung Kassel' },
            ].map((l, i) => (
              <Link key={i} to={l.to}
                className="inline-flex items-center gap-2 border border-[#e5e5e5] text-[#1f1f1f]/70 hover:border-[#befa0a] hover:text-[#1f1f1f] px-4 py-2 text-xs uppercase tracking-wider transition-colors"
                style={{ fontFamily: "'Oswald', sans-serif" }}>
                {l.label} <ArrowRight size={12} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CheckoutModal
        open={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        module="ready-check"
        productName="KI Ready Check"
        amountLabel="850 €"
      />
    </PillarLayout>
  );
}
